import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import axios from "axios";
import { AppVariant, getAppDetails } from "src/config/appVariants";
import { UserKeys } from "src/constants/userDetails";
import { getLowestPermissionRoute } from "src/constants/permissions";
import { FlexContainer } from "src/components";
import { CheckedInMark } from "src/components/CheckedIn/CheckedInMark";
import {
    CheckedInActions, CheckedInBack, CheckedInBody, CheckedInButton, CheckedInContent,
    CheckedInEyebrow, CheckedInForm, CheckedInHero, CheckedInInput, CheckedInLabel, CheckedInLink,
    CheckedInLogo, CheckedInNotice, CheckedInPage, CheckedInTextButton, CheckedInTitle, CheckedInWordmark
} from "src/components/CheckedIn/CheckedInStyles";
import logo from "src/img/checked-in/logo-horizontal-cream.png";
import { CodeInput } from "./CodeInput";
import {
    AppDetails, authenticate, authError, challengeStatus, CHALLENGE_STORAGE_KEY, EmailChallenge,
    getRegions, POLL_INTERVAL_MS, Region, requestCode, restoreChallenge, verifyCode
} from "./authApi";

interface CheckedInAuthProps {
    login: (region: string, data: AppDetails) => void;
}

export default function CheckedInAuth({ login }: CheckedInAuthProps): React.ReactElement {
    const history = useHistory();
    const location = useLocation();
    const [challenge, setChallenge] = useState<EmailChallenge | null>(restoreChallenge);
    const [regions, setRegions] = useState<Region[]>([]);
    const [regionId, setRegionId] = useState<string>("");
    const [email, setEmail] = useState<string>(challenge?.email || "");
    const [code, setCode] = useState<string>("");
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [profileVerified, setProfileVerified] = useState<boolean>(false);
    const [createdUser, setCreatedUser] = useState<AppDetails | null>(null);
    const [busy, setBusy] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const [notice, setNotice] = useState<string>("");
    const [now, setNow] = useState<number>(Date.now());
    const [invalidChallenge, setInvalidChallenge] = useState<boolean>(false);
    const [regionRetry, setRegionRetry] = useState<number>(0);
    const operation = useRef<boolean>(false);
    const mounted = useRef<boolean>(true);
    const landing: boolean = location.pathname === "/checkedIn/landing";
    const confirming: boolean = location.pathname === "/checkedIn/confirm";
    const signup: boolean = new URLSearchParams(location.search).get("mode") === "signup";
    const region: Region | undefined = regions.find(item => item.region === (challenge?.region || regionId));
    const expired: boolean = invalidChallenge || Boolean(challenge && now >= challenge.expiresAt);
    const resendSeconds: number = challenge ? Math.max(0, Math.ceil((challenge.resendAt - now) / 1000)) : 0;
    const policies = getAppDetails(AppVariant.CHECKED_IN);

    useEffect(() => {
        mounted.current = true;
        const previousTitle: string = document.title;
        document.title = "Checked In";
        return () => { mounted.current = false; document.title = previousTitle; };
    }, []);

    useEffect(() => {
        if (landing) {
            return;
        }
        let active: boolean = true;
        void getRegions().then(items => {
            if (!active) {
                return;
            }
            setRegions(items);
            const requested: string | null = new URLSearchParams(window.location.search).get("region");
            const preferred: string | null = challenge?.region || requested || localStorage.getItem(UserKeys.REGION);
            const selected: string = items.some(item => item.region === preferred) ? preferred!
                : (items.length === 1 ? items[0].region : "");
            setRegionId(selected);
            if (items.length === 0) {
                setError("Checked In is temporarily unavailable. Please try again shortly.");
            }
        }).catch(() => {
            if (active) {
                setError("We couldn’t load Checked In. Please try again.");
            }
        });
        return () => { active = false; };
    }, [landing, regionRetry]);

    useEffect(() => {
        if (challenge) {
            sessionStorage.setItem(CHALLENGE_STORAGE_KEY, JSON.stringify(challenge));
            const timer = window.setInterval(() => setNow(Date.now()), 1000);
            return () => window.clearInterval(timer);
        }
        sessionStorage.removeItem(CHALLENGE_STORAGE_KEY);
    }, [challenge]);

    useEffect(() => {
        if (confirming && !challenge && !createdUser) {
            if (localStorage.getItem(UserKeys.TOKEN)) {
                window.location.replace("/");
            } else {
                history.replace("/checkedIn/login?mode=signup");
            }
        }
    }, [confirming, challenge, createdUser, history]);

    const finishAuthentication = async (currentRegion: Region, currentChallenge: EmailChallenge): Promise<void> => {
        const data: AppDetails = await authenticate(currentRegion, currentChallenge.challengeToken, undefined);
        if (!mounted.current) {
            return;
        }
        login(currentRegion.region, data);
        sessionStorage.removeItem(CHALLENGE_STORAGE_KEY);
        setChallenge(null);
        history.replace(getLowestPermissionRoute(undefined, data.appVariant, data.isClient));
    };

    useEffect(() => {
        if (landing || !challenge || !region || expired || createdUser || (confirming && profileVerified)) {
            return;
        }
        let active: boolean = true;
        let polling: boolean = false;
        const poll = async (): Promise<void> => {
            if (operation.current || polling) {
                return;
            }
            polling = true;
            let authenticating: boolean = false;
            try {
                const status = await challengeStatus(region, challenge.challengeToken);
                if (!active || operation.current) {
                    return;
                }
                if (!status.verified) {
                    if (confirming) {
                        history.replace({ pathname: "/checkedIn/login", search: location.search });
                    }
                    return;
                }
                setError("");
                setNotice("");
                if (status.requiresProfile) {
                    setProfileVerified(true);
                    if (!confirming) {
                        history.replace({ pathname: "/checkedIn/confirm", search: location.search });
                    }
                } else {
                    authenticating = true;
                    operation.current = true;
                    setBusy(true);
                    await finishAuthentication(region, challenge);
                }
            } catch (failure) {
                if (!active || (!authenticating && operation.current)) {
                    return;
                }
                if (axios.isAxiosError(failure) && failure.response?.status === 400) {
                    setInvalidChallenge(true);
                    setError("This code has expired or is no longer available. Please request a new code.");
                } else {
                    setNotice("We couldn’t check verification. We’ll retry shortly.");
                }
            } finally {
                polling = false;
                if (authenticating) {
                    operation.current = false;
                    if (mounted.current) {
                        setBusy(false);
                    }
                }
            }
        };
        void poll();
        const timer = window.setInterval(() => { void poll(); }, POLL_INTERVAL_MS);
        return () => { active = false; window.clearInterval(timer); };
    }, [challenge, region, landing, expired, confirming, profileVerified, createdUser]);

    const changeEmail = (): void => {
        if (operation.current) {
            return;
        }
        setChallenge(null);
        setProfileVerified(false);
        setCode("");
        setError("");
        setNotice("");
        setInvalidChallenge(false);
        history.replace({ pathname: "/checkedIn/login", search: location.search });
    };

    const sendCode = async (event: FormEvent | undefined): Promise<void> => {
        event?.preventDefault();
        if (operation.current || !region) {
            return;
        }
        operation.current = true;
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const next: EmailChallenge = await requestCode(region, email.trim().toLowerCase());
            if (!mounted.current) {
                return;
            }
            setChallenge(next);
            setEmail(next.email);
            setNow(Date.now());
            setInvalidChallenge(false);
            setProfileVerified(false);
            setCode("");
            if (challenge) {
                setNotice("A new code is on its way. Please use the latest email.");
            }
        } catch (failure) {
            setError(authError(failure, "We couldn’t send your code. Please check your email address and try again."));
        } finally {
            operation.current = false;
            setBusy(false);
        }
    };

    const submitCode = async (event: FormEvent): Promise<void> => {
        event.preventDefault();
        if (!challenge || !region || operation.current || expired || code.length !== 6) {
            return;
        }
        operation.current = true;
        setBusy(true);
        setError("");
        setNotice("");
        try {
            const status = await verifyCode(region, challenge.challengeToken, code);
            if (status.verified) {
                if (status.requiresProfile) {
                    setProfileVerified(true);
                    history.replace({ pathname: "/checkedIn/confirm", search: location.search });
                } else {
                    await finishAuthentication(region, challenge);
                }
            }
        } catch (failure) {
            setError(authError(failure, "That code is incorrect or has expired. Try again or request a new code."));
        } finally {
            operation.current = false;
            setBusy(false);
        }
    };

    const submitProfile = async (event: FormEvent): Promise<void> => {
        event.preventDefault();
        if (!challenge || !region || !profileVerified || !firstName.trim() || operation.current || expired) {
            return;
        }
        operation.current = true;
        setBusy(true);
        setError("");
        try {
            const data: AppDetails = await authenticate(region, challenge.challengeToken, {
                firstName: firstName.trim(), lastName: lastName.trim(), acceptToc: true
            });
            login(region.region, data);
            setCreatedUser(data);
            setChallenge(null);
            setNotice("");
        } catch (failure) {
            setError(authError(failure, "We couldn’t create your account. Please try again."));
        } finally {
            operation.current = false;
            setBusy(false);
        }
    };

    const goToLogin = (create: boolean): void => {
        const params = new URLSearchParams(location.search);
        params.set("mode", create ? "signup" : "login");
        history.push({ pathname: "/checkedIn/login", search: params.toString() });
    };

    if (landing) {
        return (
            <CheckedInPage $dark>
                <CheckedInContent $landing>
                    <CheckedInLogo src={logo} alt="Checked In" />
                    <CheckedInHero>
                        <CheckedInMark dark={false} size={90} />
                        <CheckedInTitle $landing>We know<br /><em>a place.</em></CheckedInTitle>
                        <CheckedInBody>Hand-picked places to eat, drink, stay and wander, from people who&apos;ve
                            actually been. Free to use, and nobody pays to be recommended.</CheckedInBody>
                    </CheckedInHero>
                    <CheckedInActions>
                        <CheckedInButton $light onClick={() => goToLogin(true)}>Create a free account</CheckedInButton>
                        <CheckedInButton $outline onClick={() => goToLogin(false)}>Login</CheckedInButton>
                    </CheckedInActions>
                </CheckedInContent>
            </CheckedInPage>
        );
    }

    return (
        <CheckedInPage>
            <CheckedInContent>
                {!createdUser && <CheckedInBack type="button" aria-label="Back" disabled={busy} onClick={() => {
                    if (confirming || challenge) {
                        changeEmail();
                    } else {
                        history.push({ pathname: "/checkedIn/landing", search: location.search });
                    }
                }}><svg viewBox="0 0 24 28" fill="none" aria-hidden="true">
                        <path d="M17 3 6 14l11 11" stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                            strokeLinejoin="round" />
                    </svg></CheckedInBack>}

                {createdUser ? <>
                    <FlexContainer alignItems="center" margin="64px 0 0">
                        <CheckedInMark dark size={64} />
                        <CheckedInWordmark><img src={logo} alt="Checked In" /></CheckedInWordmark>
                    </FlexContainer>
                    <CheckedInEyebrow $center>Account created</CheckedInEyebrow>
                    <CheckedInTitle $center>You&apos;re <em>checked in,</em><br />{createdUser.firstName}.</CheckedInTitle>
                    <CheckedInBody $center>There&apos;s a seat at the table with your name on it.</CheckedInBody>
                    <CheckedInBody $center>Next time, just log in with</CheckedInBody>
                    <CheckedInBody $center>{createdUser.email}.</CheckedInBody>
                    <CheckedInActions>
                        <CheckedInButton onClick={() => history.replace(getLowestPermissionRoute(undefined, createdUser.appVariant, createdUser.isClient))}>Let&apos;s go</CheckedInButton>
                    </CheckedInActions>
                </> : confirming ? (
                    <CheckedInForm onSubmit={submitProfile}>
                        <CheckedInEyebrow>{new Date().getHours() < 12 ? "Good morning"
                            : new Date().getHours() < 18 ? "Good afternoon" : "Good evening"}</CheckedInEyebrow>
                        <CheckedInTitle>What should we <em>call you?</em></CheckedInTitle>
                        <CheckedInBody>Just so we can say hello properly. You can change this any time.</CheckedInBody>
                        <CheckedInLabel htmlFor="firstName">First name</CheckedInLabel>
                        <CheckedInInput id="firstName" autoComplete="given-name" placeholder="First name" required
                            maxLength={80} value={firstName} disabled={busy} onChange={event => setFirstName(event.target.value)} />
                        <CheckedInLabel htmlFor="lastName">Last name (optional)</CheckedInLabel>
                        <CheckedInInput id="lastName" autoComplete="family-name" placeholder="Last name"
                            maxLength={80} value={lastName} disabled={busy} onChange={event => setLastName(event.target.value)} />
                        {error && <CheckedInNotice $error role="alert">{error}</CheckedInNotice>}
                        {expired && <CheckedInNotice $error role="alert">Your session has expired. <CheckedInTextButton
                            type="button" onClick={changeEmail}>Request a new code</CheckedInTextButton></CheckedInNotice>}
                        <CheckedInActions><CheckedInButton type="submit"
                            disabled={busy || !profileVerified || !firstName.trim() || expired}>
                            {busy ? "Creating your account…" : "Continue"}
                        </CheckedInButton></CheckedInActions>
                    </CheckedInForm>
                ) : challenge ? (
                    <CheckedInForm onSubmit={submitCode}>
                        <CheckedInTitle>Check your <em>inbox.</em></CheckedInTitle>
                        <CheckedInBody>We&apos;ve sent a 6-digit code to <strong>{challenge.email}</strong>.
                            It&apos;s good for 10 minutes.</CheckedInBody>
                        <CodeInput code={code} disabled={busy || expired} onChange={setCode} />
                        <CheckedInBody>Nothing yet? Check your spam folder, or <CheckedInTextButton type="button"
                            disabled={busy || resendSeconds > 0} onClick={() => { void sendCode(undefined); }}>
                            send a new code{resendSeconds > 0 ? ` (${resendSeconds}s)` : ""}.
                        </CheckedInTextButton></CheckedInBody>
                        <CheckedInBody>Wrong email? <CheckedInTextButton type="button" disabled={busy}
                            onClick={changeEmail}>Change it</CheckedInTextButton></CheckedInBody>
                        {expired && <CheckedInNotice $error role="alert">This code has expired. Please request a new code.</CheckedInNotice>}
                        {error && <CheckedInNotice $error role="alert">{error}</CheckedInNotice>}
                        {notice && <CheckedInNotice role="status">{notice}</CheckedInNotice>}
                        <CheckedInActions><CheckedInButton type="submit" disabled={busy || expired || code.length !== 6}>
                            {busy ? "Verifying…" : "Verify"}
                        </CheckedInButton></CheckedInActions>
                    </CheckedInForm>
                ) : (
                    <CheckedInForm onSubmit={event => { void sendCode(event); }}>
                        <CheckedInTitle>{signup ? <>Pull up a <em>chair.</em></> : <>Welcome <em>back.</em></>}</CheckedInTitle>
                        <CheckedInBody>{signup ? "Create a free account to save places, plan trips and join the conversation in the Lounge."
                            : "Good to see you again. Log in the same way you signed up."}</CheckedInBody>
                        <CheckedInLabel htmlFor="email">Email</CheckedInLabel>
                        <CheckedInInput id="email" type="email" autoComplete="email" inputMode="email" required
                            maxLength={255} placeholder="you@example.com" value={email} disabled={busy}
                            onChange={event => setEmail(event.target.value)} />
                        {regions.length > 1 && !region && <>
                            <CheckedInLabel htmlFor="region">Region</CheckedInLabel>
                            <CheckedInInput as="select" id="region" value={regionId} required
                                onChange={event => setRegionId(event.target.value)}>
                                <option value="">Select your region</option>
                                {regions.map(item => <option key={item.region} value={item.region}>{item.region}</option>)}
                            </CheckedInInput>
                        </>}
                        {error && <CheckedInNotice $error role="alert">{error}{regions.length === 0 && <> <CheckedInTextButton
                            type="button" onClick={() => { setError(""); setRegionRetry(value => value + 1); }}>Try again</CheckedInTextButton></>}</CheckedInNotice>}
                        <CheckedInActions>
                            <CheckedInButton type="submit" disabled={busy || !region || !email.trim()}>
                                {busy ? "Sending your code…" : "Send me a code"}
                            </CheckedInButton>
                            <CheckedInBody $center>By continuing, you agree to the ‘Checked In’ app&apos;s <CheckedInLink
                                href={policies.termsUrl} target="_blank" rel="noreferrer">Terms of Service</CheckedInLink> and <CheckedInLink
                                href={policies.privacyUrl} target="_blank" rel="noreferrer">Privacy Policy</CheckedInLink>.</CheckedInBody>
                            <CheckedInBody $center>{signup ? "Already have an account? " : "New here? "}<CheckedInTextButton
                                type="button" onClick={() => goToLogin(!signup)}>{signup ? "Log in" : "Create a free account"}</CheckedInTextButton></CheckedInBody>
                        </CheckedInActions>
                    </CheckedInForm>
                )}
            </CheckedInContent>
        </CheckedInPage>
    );
}
